import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { useAuthGetCurrentUser } from "@/orval/auth";
import {
  getInvitationGetPendingInvitationsQueryKey,
  getInvitationGetPendingInvitationsQueryOptions,
  invitationGetPendingInvitations,
  useInvitationAcceptInvitationById,
} from "@/orval/invitation";
import { OrganizationRoleType, type OrganizationInvitationDTO } from "@/orval/model";
import { Link, createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { CheckIcon, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

const formatRoleLabel = (roleType: number) => {
  if (roleType === OrganizationRoleType.ADMIN) {
    return "Admin";
  }

  if (roleType === OrganizationRoleType.PARENT) {
    return "Parent";
  }

  if (roleType === OrganizationRoleType.TEACHER) {
    return "Teacher";
  }

  return "Member";
};

const MyInvitationsPage = () => {
  const { data: currentUser } = useAuthGetCurrentUser();
  const { mutateAsync: acceptInvitation, isPending: isAccepting } = useInvitationAcceptInvitationById();

  const getRolePortalUrl = (invitation: OrganizationInvitationDTO) => {
    if (invitation.targetRoleType === OrganizationRoleType.TEACHER) {
      return `/organizations/${invitation.organizationId}/teacher/classes`;
    }

    if (invitation.targetRoleType === OrganizationRoleType.PARENT) {
      return `/organizations/${invitation.organizationId}/parent`;
    }

    if (invitation.targetRoleType === OrganizationRoleType.ADMIN) {
      return `/organizations/${invitation.organizationId}/admin`;
    }

    return `/organizations/${invitation.organizationId}`;
  };

  const handleAcceptInvitation = async (invitation: OrganizationInvitationDTO) => {
    const result = await acceptInvitation({ invitationId: invitation.id });
    const acceptedInvitation = result.invitation;
    const roleLabel = formatRoleLabel(acceptedInvitation.targetRoleType);

    toast.success(`Invitation accepted for ${acceptedInvitation.organizationName}.`, {
      action: {
        label: `View ${roleLabel} Portal`,
        onClick: () => {
          window.location.href = getRolePortalUrl(acceptedInvitation);
        },
      },
    });
  };

  const columns = useMemo<ColumnDef<OrganizationInvitationDTO>[]>(
    () => [
      {
        accessorKey: "organizationName",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Organization" />,
        enableSorting: false,
      },
      {
        accessorKey: "targetRoleType",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
        enableSorting: true,
        cell: ({ row }) => formatRoleLabel(row.original.targetRoleType),
      },
      {
        accessorKey: "expiresAtUtc",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Expires" />,
        enableSorting: true,
        cell: ({ row }) => format(parseISO(row.original.expiresAtUtc), "PPpp"),
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => (
          <Button size="sm" onClick={() => void handleAcceptInvitation(row.original)} disabled={isAccepting}>
            <CheckIcon className="mr-2 size-4" />
            Accept
          </Button>
        ),
      },
    ],
    [handleAcceptInvitation, isAccepting],
  );

  return (
    <div className="container flex min-h-0 flex-1 flex-col pb-4">
      <h2 className="border-b py-8 text-3xl font-semibold tracking-tight text-foreground">My Invitations</h2>
      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {currentUser?.emailConfirmed === false ? (
          <Alert variant="warning">
            <TriangleAlert className="size-4" />
            <AlertTitle>Confirm your email to view invitations</AlertTitle>
            <AlertDescription>
              Your pending invitations are hidden until your email address is confirmed. Visit your{" "}
              <Link to="/my/profile" className="font-medium underline underline-offset-4">
                profile
              </Link>{" "}
              to resend the confirmation email or update your address.
            </AlertDescription>
          </Alert>
        ) : (
          <DataTable
            columns={columns}
            queryFn={invitationGetPendingInvitations}
            queryKeyFn={getInvitationGetPendingInvitationsQueryKey}
            getRowIdFn={(row) => row.id.toString()}
          />
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_app/my/invitations")({
  validateSearch: searchStringSearchSchema,
  loader: ({ location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getInvitationGetPendingInvitationsQueryOptions(buildDataTableQueryOptions({ searchString })),
    );
  },
  component: MyInvitationsPage,
});
